import { useState } from 'react';
import clsx from 'clsx';
import {
  updateWasserijItem,
  type WasserijItemDetail,
  type WasserijItemPatch,
} from '../../lib/wasserij-items';
import { bulkUpdateOrder, type ItemChange, type ItemShape } from '../../lib/orders';
import { isPerStukPrimary } from '../../lib/order-edit-rules';
import { formatCurrency } from '../../lib/format';

type KleedShape = 'Rechthoek' | 'Rond' | 'Anders';

interface KleedDraft {
  soort: string;
  merk: string;
  vorm: KleedShape;
  lengteCm: number | null;
  breedteCm: number | null;
  diameterCm: number | null;
  areaM2: number | null;
  aantal: number | null;
}

/** SF-vorm ('Rechthoek'/'Rond'/'Anders') → frontend-shape voor de bulk-update API. */
function shapeToApi(vorm: KleedShape): ItemShape {
  if (vorm === 'Rond') return 'rond';
  if (vorm === 'Anders') return 'overig';
  return 'rechthoekig';
}

/** Live m²-berekening uit de draft, zodat de medewerker het effect ziet vóór opslaan. */
function previewArea(d: KleedDraft): number | null {
  if (d.vorm === 'Rechthoek') {
    return d.lengteCm && d.breedteCm ? (d.lengteCm / 100) * (d.breedteCm / 100) : null;
  }
  if (d.vorm === 'Rond') {
    return d.diameterCm ? Math.PI * Math.pow(d.diameterCm / 200, 2) : null;
  }
  return d.areaM2 ?? null;
}

/** <select>-opties met huidige waarde vooraan (ook als die niet meer in de picklist staat). */
function withCurrent(current: string, options: string[]): string[] {
  return current && !options.includes(current) ? [current, ...options] : options;
}

interface Props {
  item: WasserijItemDetail;
  /** Aangeroepen na een geslaagde (of deels geslaagde) mutatie → parent reload + notify. */
  onSaved: () => void;
  /** Compactere padding voor de smalle drawer-context. */
  dense?: boolean;
  /** Toon een read-only Prijs-regel (drawer heeft geen aparte prijs-breakdown). */
  showPrice?: boolean;
}

/**
 * Geconsolideerde "Kleed"-kaart: toont Product/Soort/Merk/Vorm/Afmeting read-only
 * en biedt één "Bewerken"-modus om Soort, Merk, Vorm én Afmeting samen aan te passen.
 * Gedeeld door de volledige /items/:id pagina en het /orders/:id zijpaneel zodat de
 * bewerk-flow (incl. het vorm-contract en partial-failure afhandeling) op één plek leeft.
 *
 * Per-stuk items (Kussen) hebben geen soort/merk/vorm/maten — daar toont de
 * kaart Product + Aantal en bewerkt alleen het aantal (Apex herprijst per stuk).
 */
export function KleedCard({ item, onSaved, dense, showPrice }: Props) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState<KleedDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const perStuk = isPerStukPrimary(item.product.code);

  const dimsLabel = (() => {
    if (item.vorm === 'Rond' && item.diameterCm) return `Ø ${item.diameterCm} cm`;
    if (item.vorm === 'Anders') return item.areaM2 ? `${item.areaM2.toFixed(2)} m²` : '—';
    if (item.lengteCm && item.breedteCm) return `${item.lengteCm} × ${item.breedteCm} cm`;
    return '—';
  })();

  const startEdit = () => {
    const vorm: KleedShape =
      item.vorm === 'Rond' || item.vorm === 'Anders' ? item.vorm : 'Rechthoek';
    setDraft({
      soort: item.soort,
      merk: item.merk,
      vorm,
      lengteCm: item.lengteCm,
      breedteCm: item.breedteCm,
      diameterCm: item.diameterCm,
      areaM2: item.areaM2,
      aantal: item.aantal ?? 1,
    });
    setError(null);
    setEdit(true);
  };

  const cancel = () => {
    setEdit(false);
    setDraft(null);
    setError(null);
  };

  const patch = (p: Partial<KleedDraft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const save = async () => {
    if (!draft) return;

    // Per-stuk (Kussen): alleen aantal — één bulk-update change, Apex herprijst.
    if (perStuk) {
      const aantal = draft.aantal;
      if (aantal == null || !Number.isInteger(aantal) || aantal < 1) {
        setError('Aantal moet een geheel getal van minimaal 1 zijn');
        return;
      }
      if (aantal === item.aantal) {
        cancel();
        return;
      }
      setSaving(true);
      setError(null);
      try {
        await bulkUpdateOrder(item.workOrderId, [
          { kind: 'update', itemId: item.id, aantal },
        ]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Aantal opslaan mislukt');
        setSaving(false);
        return;
      }
      setSaving(false);
      setEdit(false);
      setDraft(null);
      onSaved();
      return;
    }

    // Soort/Merk → item-update (geen prijsimpact). Vorm/Afmeting → bulk-update
    // (herrekent oppervlakte + prijs + bundelkorting). Altijd vorm + volledige
    // maat-set meesturen (anders INVALID_DIMENSIONS).
    const fieldPatch: WasserijItemPatch = {};
    if (draft.soort !== item.soort) fieldPatch.Soort__c = draft.soort;
    if (draft.merk !== item.merk) fieldPatch.Merk__c = draft.merk;

    const dimsChanged =
      draft.vorm !== (item.vorm as KleedShape) ||
      draft.lengteCm !== item.lengteCm ||
      draft.breedteCm !== item.breedteCm ||
      draft.diameterCm !== item.diameterCm ||
      draft.areaM2 !== item.areaM2;

    let change: ItemChange | null = null;
    if (dimsChanged) {
      change = { kind: 'update', itemId: item.id, vorm: shapeToApi(draft.vorm) };
      if (draft.vorm === 'Rechthoek') {
        if (!draft.lengteCm || !draft.breedteCm) {
          setError('Lengte en breedte zijn verplicht bij rechthoek');
          return;
        }
        change.lengteCm = draft.lengteCm;
        change.breedteCm = draft.breedteCm;
      } else if (draft.vorm === 'Rond') {
        if (!draft.diameterCm) {
          setError('Diameter is verplicht bij rond');
          return;
        }
        change.diameterCm = draft.diameterCm;
      } else {
        if (!draft.areaM2) {
          setError('Oppervlakte is verplicht bij anders');
          return;
        }
        change.oppervlakteM2 = draft.areaM2;
      }
    }

    if (Object.keys(fieldPatch).length === 0 && !change) {
      cancel();
      return;
    }

    setSaving(true);
    setError(null);

    // 1) Soort/Merk eerst — faalt dit, dan stoppen vóór de maat-update.
    if (Object.keys(fieldPatch).length > 0) {
      try {
        await updateWasserijItem(item.id, fieldPatch);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Soort/Merk opslaan mislukt');
        setSaving(false);
        return;
      }
    }

    // 2) Vorm/Afmeting via bulk-update (recalc). Partial-failure: meld + refresh.
    if (change) {
      try {
        await bulkUpdateOrder(item.workOrderId, [change]);
      } catch (err) {
        setError(
          err instanceof Error
            ? `Afmeting niet opgeslagen — soort/merk wél. ${err.message}`
            : 'Afmeting niet opgeslagen — soort/merk wél.'
        );
        setSaving(false);
        onSaved();
        return;
      }
    }

    setSaving(false);
    setEdit(false);
    setDraft(null);
    onSaved();
  };

  return (
    <section className={clsx('bg-white rounded-lg border border-slate-200', dense ? 'p-3' : 'p-4')}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {perStuk ? 'Item' : 'Kleed'}
        </h3>
        {!edit && (
          <button
            type="button"
            onClick={startEdit}
            className="text-xs text-indigo-600 hover:underline"
          >
            Bewerken
          </button>
        )}
      </div>

      {!edit || !draft ? (
        <div>
          <Row label="Product">
            {item.product.name || '—'}
            {item.product.code && (
              <span className="text-xs text-slate-500 ml-2 font-mono">{item.product.code}</span>
            )}
          </Row>
          {!perStuk && (
            <>
              <Row label="Soort">{item.soort || '—'}</Row>
              <Row label="Merk">{item.merk || '—'}</Row>
              <Row label="Vorm">{item.vorm}</Row>
              <Row label="Afmeting">{dimsLabel}</Row>
              {item.areaM2 != null && <Row label="Oppervlakte">{`${item.areaM2.toFixed(2)} m²`}</Row>}
            </>
          )}
          <Row label="Aantal">{item.aantal ? String(item.aantal) : '—'}</Row>
          {showPrice && <Row label="Prijs">{formatCurrency(item.priceInclBtw)}</Row>}
        </div>
      ) : perStuk ? (
        <div className="space-y-3">
          <Row label="Product">{item.product.name || '—'}</Row>

          <label className="flex items-baseline gap-3 py-1 text-sm">
            <span className="w-28 text-slate-500 flex-shrink-0">Aantal</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft.aantal ?? ''}
              onChange={(e) =>
                patch({ aantal: e.target.value === '' ? null : Number(e.target.value) })
              }
              className="w-24 text-sm border border-slate-300 rounded px-2 py-1.5"
            />
          </label>

          <p className="text-xs text-slate-500">Prijs wordt herrekend bij opslaan (per stuk).</p>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Opslaan…' : 'Opslaan'}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className="text-xs text-slate-600 hover:text-slate-900"
            >
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Row label="Product">{item.product.name || '—'}</Row>

          <label className="flex items-baseline gap-3 py-1 text-sm">
            <span className="w-28 text-slate-500 flex-shrink-0">Soort</span>
            <select
              value={draft.soort}
              onChange={(e) => patch({ soort: e.target.value })}
              className="flex-1 text-sm border border-slate-300 rounded px-2 py-1.5"
            >
              <option value="">— geen —</option>
              {withCurrent(draft.soort, item.picklists.soort).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className="flex items-baseline gap-3 py-1 text-sm">
            <span className="w-28 text-slate-500 flex-shrink-0">Merk</span>
            <select
              value={draft.merk}
              onChange={(e) => patch({ merk: e.target.value })}
              className="flex-1 text-sm border border-slate-300 rounded px-2 py-1.5"
            >
              <option value="">— geen —</option>
              {withCurrent(draft.merk, item.picklists.merk).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          <div className="flex items-baseline gap-3 py-1 text-sm">
            <span className="w-28 text-slate-500 flex-shrink-0">Vorm</span>
            <div className="flex gap-2">
              {(['Rechthoek', 'Rond', 'Anders'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => patch({ vorm: v })}
                  className={clsx(
                    'px-3 py-1.5 text-xs font-medium rounded border transition-colors',
                    draft.vorm === v
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {draft.vorm === 'Rechthoek' && (
            <div className="flex items-baseline gap-3 py-1 text-sm">
              <span className="w-28 text-slate-500 flex-shrink-0">Afmeting</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={draft.lengteCm ?? ''}
                  onChange={(e) => patch({ lengteCm: Number(e.target.value) || null })}
                  placeholder="Lengte"
                  className="w-24 text-sm border border-slate-300 rounded px-2 py-1.5"
                />
                <span className="text-slate-400">×</span>
                <input
                  type="number"
                  min={1}
                  value={draft.breedteCm ?? ''}
                  onChange={(e) => patch({ breedteCm: Number(e.target.value) || null })}
                  placeholder="Breedte"
                  className="w-24 text-sm border border-slate-300 rounded px-2 py-1.5"
                />
                <span className="text-xs text-slate-400">cm</span>
              </div>
            </div>
          )}
          {draft.vorm === 'Rond' && (
            <div className="flex items-baseline gap-3 py-1 text-sm">
              <span className="w-28 text-slate-500 flex-shrink-0">Diameter</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={draft.diameterCm ?? ''}
                  onChange={(e) => patch({ diameterCm: Number(e.target.value) || null })}
                  placeholder="Ø"
                  className="w-24 text-sm border border-slate-300 rounded px-2 py-1.5"
                />
                <span className="text-xs text-slate-400">cm</span>
              </div>
            </div>
          )}
          {draft.vorm === 'Anders' && (
            <div className="flex items-baseline gap-3 py-1 text-sm">
              <span className="w-28 text-slate-500 flex-shrink-0">Oppervlakte</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min={0.01}
                  value={draft.areaM2 ?? ''}
                  onChange={(e) => patch({ areaM2: Number(e.target.value) || null })}
                  className="w-24 text-sm border border-slate-300 rounded px-2 py-1.5"
                />
                <span className="text-xs text-slate-400">m²</span>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-500">
            Oppervlakte:{' '}
            <span className="font-medium text-slate-700">
              {previewArea(draft) != null ? `${previewArea(draft)!.toFixed(2)} m²` : '—'}
            </span>
            <span className="text-slate-400"> · prijs wordt herrekend bij opslaan</span>
          </p>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Opslaan…' : 'Opslaan'}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className="text-xs text-slate-600 hover:text-slate-900"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 py-1 text-sm">
      <span className="w-28 text-slate-500 flex-shrink-0">{label}</span>
      <span className="text-slate-900">{children}</span>
    </div>
  );
}
